import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, CheckCircle, Edit3, Plus, Trash2, ArrowRight } from 'lucide-react';
import {
  ChangeableField,
  FIELD_LABELS,
  createChangeRequest,
  getMyChangeRequests,
  type ChangeRequest,
  type CreateChangeRequestDto,
} from '@/services/changeRequestService';
import {
  fetchSeriesBac,
  fetchProvinces,
  fetchDiplomes,
  fetchCountries,
  type NomenclatureSerieBac,
  type NomenclatureProvince,
  type NomenclatureDiplome,
  type NomenclatureCountry,
} from '@/services/nomenclatureService';
import {
  MentionDiplomeLabels,
  ResidentStatusLabels,
  FonctionnaireStatusLabels,
  MobiliteStatusLabels,
} from '@/types/etudiant.types';

interface ChangeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentData: Record<string, unknown>;
  onSuccess?: () => void;
}

// Queued change item
interface QueuedChange {
  id: string;
  fieldName: ChangeableField;
  currentValue: string;
  newValue: string;
  reason?: string;
}

// Mapping from ChangeableField to studentData keys (for student-specific modal)
// Note: Staff uses StaffChangeRequestModal which has its own mapping
const fieldToDataKey: Partial<Record<ChangeableField, string>> = {
  [ChangeableField.FIRST_NAME]: 'prenom',
  [ChangeableField.LAST_NAME]: 'nom',
  [ChangeableField.FIRST_NAME_ARABIC]: 'prenomArabe',
  [ChangeableField.LAST_NAME_ARABIC]: 'nomArabe',
  [ChangeableField.PHONE]: 'telephone',
  [ChangeableField.ADDRESS]: 'adresse',
  [ChangeableField.CITY]: 'ville',
  [ChangeableField.POSTAL_CODE]: 'codePostal',
  [ChangeableField.EMERGENCY_NAME]: 'urgenceNom',
  [ChangeableField.EMERGENCY_PHONE]: 'urgenceNumero',
  [ChangeableField.EMERGENCY_ADDRESS]: 'urgenceAdresse',
  [ChangeableField.EMERGENCY_RELATION]: 'urgenceRelation',
  [ChangeableField.STUDENT_CNIE]: 'cnie',
  [ChangeableField.STUDENT_PASSPORT]: 'passport',
  [ChangeableField.STUDENT_DATE_OF_BIRTH]: 'dateNaissance',
  [ChangeableField.STUDENT_NATIONALITY]: 'paysOrigine',
  [ChangeableField.STUDENT_GENDER]: 'sexe',
  [ChangeableField.STUDENT_EMAIL]: 'email',
  [ChangeableField.STUDENT_PHONE]: 'telephone',
  [ChangeableField.STUDENT_ADDRESS]: 'adresse',
  [ChangeableField.STUDENT_CITY]: 'ville',
  [ChangeableField.STUDENT_POSTAL_CODE]: 'codePostal',
  [ChangeableField.STUDENT_MASSAR_CNE]: 'numeroMassarCNE',
  [ChangeableField.STUDENT_SERIE_BAC]: 'serieBaccalaureat',
  [ChangeableField.STUDENT_ANNEE_BAC]: 'anneeObtentionBac',
  [ChangeableField.STUDENT_DIPLOME_ACCES]: 'diplomeAcces',
  [ChangeableField.STUDENT_SPECIALITE_DIPLOME]: 'specialiteDiplomeAcces',
  [ChangeableField.STUDENT_MENTION_DIPLOME]: 'mentionDiplomeAcces',
  [ChangeableField.STUDENT_LIEU_DIPLOME]: 'lieuObtentionDiplomeAcces',
  [ChangeableField.STUDENT_RESIDENT]: 'resident',
  [ChangeableField.STUDENT_FONCTIONNAIRE]: 'fonctionnaire',
  [ChangeableField.STUDENT_BOURSE]: 'pourcentageBourse',
  [ChangeableField.STUDENT_MOBILITE]: 'mobilite',
};

// Define field input types
type FieldInputType = 'text' | 'email' | 'tel' | 'date' | 'number' | 'select' | 'nomenclature' | 'arabic';

interface FieldConfig {
  inputType: FieldInputType;
  nomenclatureType?: 'seriesBac' | 'provinces' | 'diplomes' | 'countries';
  options?: { value: string; label: string }[];
  placeholder?: string;
  min?: number;
  max?: number;
}

// Static options for simple select fields
const genderOptions = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' },
];

const mentionOptions = Object.entries(MentionDiplomeLabels).map(([value, label]) => ({
  value,
  label,
}));

const residentOptions = Object.entries(ResidentStatusLabels).map(([value, label]) => ({
  value,
  label,
}));

const fonctionnaireOptions = Object.entries(FonctionnaireStatusLabels).map(([value, label]) => ({
  value,
  label,
}));

const mobiliteOptions = Object.entries(MobiliteStatusLabels).map(([value, label]) => ({
  value,
  label,
}));

// Field configurations - determines what input type to use for each field
const fieldConfigs: Partial<Record<ChangeableField, FieldConfig>> = {
  // Text fields
  [ChangeableField.FIRST_NAME]: { inputType: 'text', placeholder: 'Prénom' },
  [ChangeableField.LAST_NAME]: { inputType: 'text', placeholder: 'Nom' },
  // Arabic name fields - use special arabic input type
  [ChangeableField.FIRST_NAME_ARABIC]: { inputType: 'arabic', placeholder: 'الاسم الشخصي' },
  [ChangeableField.LAST_NAME_ARABIC]: { inputType: 'arabic', placeholder: 'الاسم العائلي' },
  [ChangeableField.ADDRESS]: { inputType: 'text', placeholder: 'Adresse complète' },
  [ChangeableField.CITY]: { inputType: 'text', placeholder: 'Ville' },
  [ChangeableField.POSTAL_CODE]: { inputType: 'text', placeholder: 'Code postal' },
  [ChangeableField.EMERGENCY_NAME]: { inputType: 'text', placeholder: 'Nom du contact' },
  [ChangeableField.EMERGENCY_ADDRESS]: { inputType: 'text', placeholder: 'Adresse' },
  [ChangeableField.EMERGENCY_RELATION]: { inputType: 'text', placeholder: 'Ex: Père, Mère, Frère...' },
  [ChangeableField.STUDENT_CNIE]: { inputType: 'text', placeholder: 'Ex: AB123456' },
  [ChangeableField.STUDENT_PASSPORT]: { inputType: 'text', placeholder: 'Numéro de passeport' },
  [ChangeableField.STUDENT_ADDRESS]: { inputType: 'text', placeholder: 'Adresse complète' },
  [ChangeableField.STUDENT_CITY]: { inputType: 'text', placeholder: 'Ville' },
  [ChangeableField.STUDENT_POSTAL_CODE]: { inputType: 'text', placeholder: 'Code postal' },
  [ChangeableField.STUDENT_MASSAR_CNE]: { inputType: 'text', placeholder: 'Ex: A123456789' },
  [ChangeableField.STUDENT_SPECIALITE_DIPLOME]: { inputType: 'text', placeholder: 'Ex: Sciences Économiques' },
  [ChangeableField.STUDENT_ANNEE_BAC]: { inputType: 'text', placeholder: 'Ex: 2020/2021' },

  // Email fields
  [ChangeableField.STUDENT_EMAIL]: { inputType: 'email', placeholder: 'email@exemple.com' },

  // Phone fields
  [ChangeableField.PHONE]: { inputType: 'tel', placeholder: '+212 6XX XXX XXX' },
  [ChangeableField.EMERGENCY_PHONE]: { inputType: 'tel', placeholder: '+212 6XX XXX XXX' },
  [ChangeableField.STUDENT_PHONE]: { inputType: 'tel', placeholder: '+212 6XX XXX XXX' },

  // Date fields
  [ChangeableField.STUDENT_DATE_OF_BIRTH]: { inputType: 'date' },

  // Number fields
  [ChangeableField.STUDENT_BOURSE]: { inputType: 'number', min: 0, max: 100, placeholder: '0-100' },

  // Simple select fields (static options)
  [ChangeableField.STUDENT_GENDER]: { inputType: 'select', options: genderOptions },
  [ChangeableField.STUDENT_MENTION_DIPLOME]: { inputType: 'select', options: mentionOptions },
  [ChangeableField.STUDENT_RESIDENT]: { inputType: 'select', options: residentOptions },
  [ChangeableField.STUDENT_FONCTIONNAIRE]: { inputType: 'select', options: fonctionnaireOptions },
  [ChangeableField.STUDENT_MOBILITE]: { inputType: 'select', options: mobiliteOptions },

  // Nomenclature-based select fields (loaded from API)
  [ChangeableField.STUDENT_SERIE_BAC]: { inputType: 'nomenclature', nomenclatureType: 'seriesBac' },
  [ChangeableField.STUDENT_LIEU_DIPLOME]: { inputType: 'nomenclature', nomenclatureType: 'provinces' },
  [ChangeableField.STUDENT_DIPLOME_ACCES]: { inputType: 'nomenclature', nomenclatureType: 'diplomes' },
  [ChangeableField.STUDENT_NATIONALITY]: { inputType: 'nomenclature', nomenclatureType: 'countries' },
};

// Group fields for organized display
const fieldGroups = [
  {
    title: 'Identité',
    fields: [
      ChangeableField.FIRST_NAME,
      ChangeableField.LAST_NAME,
      ChangeableField.FIRST_NAME_ARABIC,
      ChangeableField.LAST_NAME_ARABIC,
      ChangeableField.STUDENT_CNIE,
      ChangeableField.STUDENT_PASSPORT,
      ChangeableField.STUDENT_DATE_OF_BIRTH,
      ChangeableField.STUDENT_NATIONALITY,
      ChangeableField.STUDENT_GENDER,
    ],
  },
  {
    title: 'Coordonnées',
    fields: [
      ChangeableField.STUDENT_EMAIL,
      ChangeableField.STUDENT_PHONE,
      ChangeableField.STUDENT_ADDRESS,
    ],
  },
  {
    title: 'Contact d\'urgence',
    fields: [
      ChangeableField.EMERGENCY_NAME,
      ChangeableField.EMERGENCY_PHONE,
      ChangeableField.EMERGENCY_ADDRESS,
      ChangeableField.EMERGENCY_RELATION,
    ],
  },
  {
    title: 'Académique',
    fields: [
      ChangeableField.STUDENT_MASSAR_CNE,
    ],
  },
  {
    title: 'Diplôme d\'accès',
    fields: [
      ChangeableField.STUDENT_SERIE_BAC,
      ChangeableField.STUDENT_ANNEE_BAC,
      ChangeableField.STUDENT_DIPLOME_ACCES,
      ChangeableField.STUDENT_SPECIALITE_DIPLOME,
      ChangeableField.STUDENT_MENTION_DIPLOME,
      ChangeableField.STUDENT_LIEU_DIPLOME,
    ],
  },
  {
    title: 'Statut & Aides',
    fields: [
      ChangeableField.STUDENT_RESIDENT,
      ChangeableField.STUDENT_FONCTIONNAIRE,
      ChangeableField.STUDENT_BOURSE,
      ChangeableField.STUDENT_MOBILITE,
    ],
  },
];

export const ChangeRequestModal: React.FC<ChangeRequestModalProps> = ({
  isOpen,
  onClose,
  studentData,
  onSuccess,
}) => {
  // Steps: 'select' -> 'form' -> 'review' -> 'success'
  const [step, setStep] = useState<'select' | 'form' | 'review' | 'success'>('select');
  const [selectedField, setSelectedField] = useState<ChangeableField | null>(null);
  const [newValue, setNewValue] = useState('');
  const [reason, setReason] = useState('');
  const [, setIsLoading] = useState(false);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [isLoadingNomenclature, setIsLoadingNomenclature] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<ChangeRequest[]>([]);

  // Queue for multiple changes
  const [queuedChanges, setQueuedChanges] = useState<QueuedChange[]>([]);
  const [submissionProgress, setSubmissionProgress] = useState({ current: 0, total: 0 });
  const [submissionErrors, setSubmissionErrors] = useState<string[]>([]);

  // Nomenclature data
  const [seriesBac, setSeriesBac] = useState<NomenclatureSerieBac[]>([]);
  const [provinces, setProvinces] = useState<NomenclatureProvince[]>([]);
  const [diplomes, setDiplomes] = useState<NomenclatureDiplome[]>([]);
  const [countries, setCountries] = useState<NomenclatureCountry[]>([]);

  // Load existing pending requests
  useEffect(() => {
    if (isOpen) {
      loadPendingRequests();
    }
  }, [isOpen]);

  const loadPendingRequests = async () => {
    setIsLoadingRequests(true);
    try {
      const requests = await getMyChangeRequests();
      setPendingRequests(requests.filter(r => r.status === 'PENDING'));
    } catch (err) {
      console.error('Error loading pending requests:', err);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // Load nomenclature data when needed
  const loadNomenclatureData = async (type: string) => {
    setIsLoadingNomenclature(true);
    try {
      switch (type) {
        case 'seriesBac':
          if (seriesBac.length === 0) {
            const data = await fetchSeriesBac();
            setSeriesBac(data || []);
          }
          break;
        case 'provinces':
          if (provinces.length === 0) {
            const data = await fetchProvinces();
            setProvinces(data || []);
          }
          break;
        case 'diplomes':
          if (diplomes.length === 0) {
            const data = await fetchDiplomes();
            setDiplomes(data || []);
          }
          break;
        case 'countries':
          if (countries.length === 0) {
            const data = await fetchCountries();
            setCountries(data || []);
          }
          break;
      }
    } catch (err) {
      console.error('Error loading nomenclature:', err);
    } finally {
      setIsLoadingNomenclature(false);
    }
  };

  const getCurrentValue = (field: ChangeableField): string => {
    const key = fieldToDataKey[field];
    if (!key) return '';
    const value = studentData[key];
    if (value === undefined || value === null) return '';
    if (typeof value === 'number') return value.toString();
    return String(value);
  };

  const getDisplayValue = (field: ChangeableField, value: string): string => {
    if (!value) return '(non renseigné)';

    const config = fieldConfigs[field];
    if (!config) return value;

    // For select fields, show the label instead of the value
    if (config.inputType === 'select' && config.options) {
      const option = config.options.find(o => o.value === value);
      return option?.label || value;
    }

    // For nomenclature fields, try to get the label
    if (config.inputType === 'nomenclature') {
      switch (config.nomenclatureType) {
        case 'seriesBac': {
          const serie = seriesBac.find(s => s.code === value);
          return serie?.labelFr || value;
        }
        case 'provinces': {
          const province = provinces.find(p => p.nameFr === value || p.id === value);
          return province?.nameFr || value;
        }
        case 'diplomes': {
          const diplome = diplomes.find(d => d.id === value || d.labelFr === value);
          return diplome?.labelFr || value;
        }
        case 'countries': {
          const country = countries.find(c => c.code === value || c.nameFr === value);
          return country?.nameFr || value;
        }
      }
    }

    return value;
  };

  const hasPendingRequest = (field: ChangeableField): boolean => {
    return pendingRequests.some(r => r.fieldName === field);
  };

  const isFieldQueued = (field: ChangeableField): boolean => {
    return queuedChanges.some(q => q.fieldName === field);
  };

  const handleFieldSelect = async (field: ChangeableField) => {
    if (hasPendingRequest(field) || isFieldQueued(field)) return;

    setSelectedField(field);
    setNewValue(getCurrentValue(field));
    setReason('');
    setError(null);

    // Load nomenclature data if needed
    const config = fieldConfigs[field];
    if (config?.inputType === 'nomenclature' && config.nomenclatureType) {
      await loadNomenclatureData(config.nomenclatureType);
    }

    setStep('form');
  };

  const handleAddToQueue = () => {
    if (!selectedField) return;

    const currentValue = getCurrentValue(selectedField);

    // Normalize values for comparison (trim whitespace, case-insensitive)
    const normalizedCurrent = (currentValue ?? '').toString().trim().toLowerCase();
    const normalizedNew = (newValue ?? '').toString().trim().toLowerCase();

    if (normalizedNew === normalizedCurrent) {
      setError('La nouvelle valeur doit être différente de la valeur actuelle');
      return;
    }

    if (!newValue.trim()) {
      setError('La nouvelle valeur ne peut pas être vide');
      return;
    }

    // Add to queue
    const newChange: QueuedChange = {
      id: `${selectedField}-${Date.now()}`,
      fieldName: selectedField,
      currentValue,
      newValue: newValue.trim(),
      reason: reason.trim() || undefined,
    };

    setQueuedChanges(prev => [...prev, newChange]);

    // Reset form and go back to select
    setSelectedField(null);
    setNewValue('');
    setReason('');
    setError(null);
    setStep('select');
  };

  const handleRemoveFromQueue = (id: string) => {
    setQueuedChanges(prev => prev.filter(q => q.id !== id));
  };

  const handleSubmitAll = async () => {
    if (queuedChanges.length === 0) return;

    setIsLoading(true);
    setError(null);
    setSubmissionErrors([]);
    setSubmissionProgress({ current: 0, total: queuedChanges.length });
    setStep('review');

    const errors: string[] = [];

    for (let i = 0; i < queuedChanges.length; i++) {
      const change = queuedChanges[i];
      setSubmissionProgress({ current: i + 1, total: queuedChanges.length });

      try {
        const dto: CreateChangeRequestDto = {
          fieldName: change.fieldName,
          requestedValue: change.newValue,
          reason: change.reason,
        };
        await createChangeRequest(dto);
      } catch (err) {
        const fieldLabel = FIELD_LABELS[change.fieldName];
        const errorMsg = err instanceof Error ? err.message : 'Erreur inconnue';
        errors.push(`${fieldLabel}: ${errorMsg}`);
      }
    }

    setIsLoading(false);
    setSubmissionErrors(errors);

    // Always go to success step to show results (success or errors)
    setStep('success');

    if (errors.length === 0) {
      // All succeeded - auto close after delay
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 2000);
    } else if (errors.length < queuedChanges.length) {
      // Some succeeded, some failed - don't auto close, let user see the errors
      onSuccess?.();
    }
    // If all failed, just show the errors (no onSuccess call)
  };

  const handleClose = () => {
    setStep('select');
    setSelectedField(null);
    setNewValue('');
    setReason('');
    setError(null);
    setQueuedChanges([]);
    setSubmissionProgress({ current: 0, total: 0 });
    setSubmissionErrors([]);
    onClose();
  };

  const handleBack = () => {
    setSelectedField(null);
    setNewValue('');
    setReason('');
    setError(null);
    setStep('select');
  };

  // Get options for nomenclature fields
  // IMPORTANT: value should match what's stored in the student profile (labelFr/nameFr, not codes)
  const getNomenclatureOptions = (type: string): { value: string; label: string }[] => {
    switch (type) {
      case 'seriesBac':
        // Store labelFr (e.g., "Sciences Mathématiques A") not code
        return seriesBac.map(s => {
          const isEtranger = s.type?.toLowerCase().includes('etranger') || s.type?.toLowerCase().includes('étranger');
          return {
            value: s.labelFr,
            label: `${isEtranger ? 'Étranger' : 'Marocain'} - ${s.labelFr}`,
          };
        });
      case 'provinces':
        return provinces.map(p => ({
          value: p.nameFr,
          label: p.nameFr,
        }));
      case 'diplomes':
        return diplomes.map(d => ({
          value: d.labelFr,
          label: d.labelFr,
        }));
      case 'countries':
        // Store nameFr (e.g., "Maroc") not code (e.g., "MA")
        return countries.map(c => ({
          value: c.nameFr,
          label: c.nameFr,
        }));
      default:
        return [];
    }
  };

  // Render the appropriate input based on field configuration
  const renderInput = () => {
    if (!selectedField) return null;

    const config = fieldConfigs[selectedField] || { inputType: 'text' };
    const inputClassName = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

    switch (config.inputType) {
      case 'select':
        return (
          <select
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
          >
            <option value="">Sélectionner...</option>
            {config.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'nomenclature':
        if (isLoadingNomenclature) {
          return (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 text-primary-600 animate-spin mr-2" />
              <span className="text-gray-500">Chargement des options...</span>
            </div>
          );
        }
        const nomenclatureOptions = getNomenclatureOptions(config.nomenclatureType || '');
        return (
          <select
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
          >
            <option value="">Sélectionner...</option>
            {nomenclatureOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            min={config.min}
            max={config.max}
            className={inputClassName}
            placeholder={config.placeholder}
          />
        );

      case 'email':
        return (
          <input
            type="email"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
            placeholder={config.placeholder || 'email@exemple.com'}
          />
        );

      case 'tel':
        return (
          <input
            type="tel"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
            placeholder={config.placeholder || '+212 6XX XXX XXX'}
          />
        );

      case 'arabic':
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              className={`${inputClassName} text-right`}
              placeholder={config.placeholder || 'أدخل النص بالعربية'}
              dir="rtl"
              lang="ar"
            />
            {/* Arabic Virtual Keyboard */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500 mb-2 text-center">لوحة المفاتيح العربية</p>
              <div className="grid grid-cols-11 gap-1">
                {/* Row 1 */}
                {['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'].map((char) => (
                  <button
                    key={char}
                    type="button"
                    onClick={() => setNewValue(prev => prev + char)}
                    className="p-2 text-lg bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  >
                    {char}
                  </button>
                ))}
                {/* Row 2 */}
                {['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ة'].map((char) => (
                  <button
                    key={char}
                    type="button"
                    onClick={() => setNewValue(prev => prev + char)}
                    className="p-2 text-lg bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  >
                    {char}
                  </button>
                ))}
                {/* Row 3 */}
                {['ئ', 'ء', 'ؤ', 'ر', 'ى', 'ة', 'و', 'ز', 'ظ', 'ط', 'ذ'].map((char, idx) => (
                  <button
                    key={`${char}-${idx}`}
                    type="button"
                    onClick={() => setNewValue(prev => prev + char)}
                    className="p-2 text-lg bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  >
                    {char}
                  </button>
                ))}
                {/* Row 4 - Special characters */}
                {['د', 'ـ', 'إ', 'أ', 'آ', 'ٱ'].map((char) => (
                  <button
                    key={char}
                    type="button"
                    onClick={() => setNewValue(prev => prev + char)}
                    className="p-2 text-lg bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  >
                    {char}
                  </button>
                ))}
                {/* Space button */}
                <button
                  type="button"
                  onClick={() => setNewValue(prev => prev + ' ')}
                  className="col-span-3 p-2 text-sm bg-white border border-gray-300 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                >
                  مسافة
                </button>
                {/* Backspace button */}
                <button
                  type="button"
                  onClick={() => setNewValue(prev => prev.slice(0, -1))}
                  className="col-span-2 p-2 text-sm bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors text-red-600"
                >
                  ⌫ حذف
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className={inputClassName}
            placeholder={config.placeholder || 'Entrez la nouvelle valeur'}
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Edit3 className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Demander une modification
              </h2>
              <p className="text-sm text-gray-500">
                {step === 'select'
                  ? 'Sélectionnez les champs à modifier'
                  : step === 'form'
                  ? FIELD_LABELS[selectedField!]
                  : step === 'review'
                  ? 'Envoi des demandes...'
                  : 'Demandes envoyées'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'success' ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {submissionErrors.length === 0
                  ? 'Demandes envoyées'
                  : `${queuedChanges.length - submissionErrors.length} demande(s) envoyée(s)`}
              </h3>
              <p className="text-gray-500 text-center mb-4">
                {submissionErrors.length === 0
                  ? 'Vos demandes de modification ont été soumises. Vous serez notifié une fois qu\'elles auront été traitées.'
                  : 'Certaines demandes ont été soumises avec succès.'}
              </p>
              {submissionErrors.length > 0 && (
                <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700 mb-2">
                    {submissionErrors.length} erreur(s):
                  </p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {submissionErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Show close button when there are errors or after viewing success */}
              {submissionErrors.length > 0 && (
                <button
                  onClick={handleClose}
                  className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Fermer
                </button>
              )}
            </div>
          ) : step === 'review' ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-12 h-12 text-primary-600 animate-spin mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Envoi des demandes...
              </h3>
              <p className="text-gray-500">
                {submissionProgress.current} / {submissionProgress.total}
              </p>
              <div className="w-full max-w-xs mt-4">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${(submissionProgress.current / submissionProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ) : step === 'select' ? (
            <>
              {/* Queued changes summary */}
              {queuedChanges.length > 0 && (
                <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-primary-800">
                      Modifications à soumettre ({queuedChanges.length})
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {queuedChanges.map((change) => (
                      <div
                        key={change.id}
                        className="flex items-center justify-between p-2 bg-white rounded-lg border border-primary-100"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {FIELD_LABELS[change.fieldName]}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {getDisplayValue(change.fieldName, change.currentValue)} <ArrowRight className="inline w-3 h-3" /> {getDisplayValue(change.fieldName, change.newValue)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRemoveFromQueue(change.id)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isLoadingRequests ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
                </div>
              ) : (
                <div className="space-y-6">
                  {fieldGroups.map((group) => (
                    <div key={group.title}>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">{group.title}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {group.fields.map((field) => {
                          const isPending = hasPendingRequest(field);
                          const isQueued = isFieldQueued(field);
                          const currentValue = getCurrentValue(field);
                          const displayValue = getDisplayValue(field, currentValue);
                          return (
                            <button
                              key={field}
                              onClick={() => handleFieldSelect(field)}
                              disabled={isPending || isQueued}
                              className={`flex flex-col items-start p-3 rounded-lg border text-left transition-colors ${
                                isPending
                                  ? 'border-orange-200 bg-orange-50 cursor-not-allowed'
                                  : isQueued
                                  ? 'border-primary-200 bg-primary-50 cursor-not-allowed'
                                  : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                              }`}
                            >
                              <span className="text-sm font-medium text-gray-900">
                                {FIELD_LABELS[field]}
                              </span>
                              <span className="text-xs text-gray-500 truncate max-w-full">
                                {displayValue}
                              </span>
                              {isPending && (
                                <span className="text-xs text-orange-600 mt-1">
                                  Demande en attente
                                </span>
                              )}
                              {isQueued && (
                                <span className="text-xs text-primary-600 mt-1 flex items-center gap-1">
                                  <Plus className="w-3 h-3" /> Ajouté
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Current value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valeur actuelle
                </label>
                <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-600">
                  {getDisplayValue(selectedField!, getCurrentValue(selectedField!))}
                </div>
              </div>

              {/* New value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouvelle valeur <span className="text-red-500">*</span>
                </label>
                {renderInput()}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison de la modification (optionnel)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  placeholder="Expliquez pourquoi vous souhaitez modifier cette information..."
                />
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  Votre demande sera examinée par l'administration. Vous serez notifié une fois la décision prise.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'success' && step !== 'review' && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            {step === 'form' ? (
              <>
                <button
                  onClick={handleBack}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Retour
                </button>
                <button
                  onClick={handleAddToQueue}
                  disabled={!newValue.trim()}
                  className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter à la liste
                </button>
              </>
            ) : (
              <div className="flex items-center justify-between w-full">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Fermer
                </button>
                {queuedChanges.length > 0 && (
                  <button
                    onClick={handleSubmitAll}
                    className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Soumettre {queuedChanges.length} demande{queuedChanges.length > 1 ? 's' : ''}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangeRequestModal;
